import { useEffect, useState } from 'react';
import { Loader2, Type } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  REELS_SHORTS_TITLE_TEMPLATES,
  TITLE_ALERT_KICKER,
  TITLE_BLACK_CAPSULE,
  TITLE_FILL_TEMPLATES,
  TITLE_FONT_FAMILIES,
  TITLE_GRADIENT_BORDER,
  TITLE_IMPACT_STACK,
  TITLE_MARKER_HIGHLIGHT,
  TITLE_NUMBER_HOOK,
  TITLE_YELLOW_BANNER,
  displayTitleText,
  sanitizeTitleOverlay,
  sanitizeTitleStyle,
  titleFillCss,
  titleTypeCss,
} from '@/lib/reelsShortsTitle';

function clip(text, n) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  return raw.length > n ? `${raw.slice(0, n - 1)}…` : raw;
}

function GradientChip({ fill, radius = 6, className = '', children, padding = 1.5 }) {
  return (
    <div
      className={className}
      style={{
        borderRadius: radius,
        padding,
        background: TITLE_GRADIENT_BORDER,
      }}
    >
      <div
        className="h-full w-full"
        style={{
          borderRadius: Math.max(1, radius - padding),
          background: fill,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function TitleStyleControls({ style, disabled = false, onChange }) {
  const next = sanitizeTitleStyle(style);
  const set = (patch, message) => {
    const merged = sanitizeTitleStyle({ ...next, ...patch });
    onChange?.(merged, message);
  };

  return (
    <div className="space-y-3" data-testid="reels-title-style-controls">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Font</span>
          <select
            value={next.font_family}
            disabled={disabled}
            onChange={(event) => set({ font_family: event.target.value }, 'Title font saved')}
            className="h-8 w-full rounded-md border border-[#2a3446] bg-black/40 px-2 text-xs text-white"
            data-testid="reels-title-font-family"
          >
            {TITLE_FONT_FAMILIES.map((row) => (
              <option key={row.id} value={row.id}>{row.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Size {Math.round(next.font_size * 100)}%</span>
          <input
            type="range"
            min="0.7"
            max="1.5"
            step="0.05"
            value={next.font_size}
            disabled={disabled}
            onChange={(event) => set({ font_size: Number(event.target.value) }, 'Title size saved')}
            className="h-8 w-full"
            data-testid="reels-title-font-size"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Pad X {next.pad_x}</span>
          <input
            type="range"
            min="24"
            max="96"
            step="2"
            value={next.pad_x}
            disabled={disabled}
            onChange={(event) => set({ pad_x: Number(event.target.value) }, 'Title padding saved')}
            className="h-8 w-full"
            data-testid="reels-title-pad-x"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Pad Y {next.pad_y}</span>
          <input
            type="range"
            min="12"
            max="56"
            step="2"
            value={next.pad_y}
            disabled={disabled}
            onChange={(event) => set({ pad_y: Number(event.target.value) }, 'Title padding saved')}
            className="h-8 w-full"
            data-testid="reels-title-pad-y"
          />
        </label>
      </div>
      <label className="inline-flex items-center gap-2 text-[11px] text-slate-300">
        <input
          type="checkbox"
          checked={next.uppercase}
          disabled={disabled}
          onChange={(event) => set({ uppercase: event.target.checked }, 'Title case saved')}
          data-testid="reels-title-uppercase"
        />
        Uppercase
      </label>
      <div>
        <p className="mb-1.5 text-[10px] uppercase tracking-wide text-slate-500">Gradient background</p>
        <div className="flex flex-wrap gap-1.5">
          {TITLE_FILL_TEMPLATES.map((row) => {
            const selected = next.fill_template === row.id;
            return (
              <button
                key={row.id}
                type="button"
                disabled={disabled}
                title={row.label}
                onClick={() => set({
                  fill_template: row.id,
                  fill_color: row.color,
                  fill_color2: row.color2,
                  text_color: row.ink,
                }, `${row.label} title fill`)}
                className={`h-8 min-w-[52px] rounded-md border px-1.5 text-[10px] font-semibold uppercase tracking-wide ${
                  selected ? 'border-yellow-300 ring-1 ring-yellow-300/50' : 'border-white/10'
                } disabled:opacity-50`}
                style={{ background: `linear-gradient(135deg, ${row.color}, ${row.color2})`, color: row.ink }}
                data-testid={`reels-title-fill-${row.id}`}
              >
                {row.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Color 1</span>
          <input
            type="color"
            value={next.fill_color}
            disabled={disabled}
            onChange={(event) => set({ fill_template: 'custom', fill_color: event.target.value }, 'Title color saved')}
            className="h-8 w-full cursor-pointer rounded border border-[#2a3446] bg-black/40"
            data-testid="reels-title-fill-color"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Color 2</span>
          <input
            type="color"
            value={next.fill_color2}
            disabled={disabled}
            onChange={(event) => set({ fill_template: 'custom', fill_color2: event.target.value }, 'Title color saved')}
            className="h-8 w-full cursor-pointer rounded border border-[#2a3446] bg-black/40"
            data-testid="reels-title-fill-color2"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Text</span>
          <input
            type="color"
            value={next.text_color}
            disabled={disabled}
            onChange={(event) => set({ fill_template: 'custom', text_color: event.target.value }, 'Title text color saved')}
            className="h-8 w-full cursor-pointer rounded border border-[#2a3446] bg-black/40"
            data-testid="reels-title-text-color"
          />
        </label>
      </div>
    </div>
  );
}

function TitlePreview({ templateId, line1, line2 }) {
  const top = clip(line1, 22) || '6 CANCER CAUSING';
  const bottom = clip(line2, 26) || 'BEAUTY / SKINCARE PRODUCTS';
  const numberMatch = `${line1} ${line2}`.trim().match(/^(\d+)\s*(.*)$/);
  const number = numberMatch?.[1] || '6';
  const rest = clip(numberMatch?.[2] || bottom, 18);

  return (
    <div className="relative w-[72px] h-[128px] shrink-0 rounded-lg overflow-hidden bg-gradient-to-b from-slate-600 to-slate-900 ring-1 ring-white/10">
      <div className="absolute inset-x-0 bottom-3 mx-auto h-9 w-9 rounded-full bg-slate-500/80" />
      {templateId === TITLE_IMPACT_STACK ? (
        <div className="absolute top-2 inset-x-1 space-y-1">
          <GradientChip fill="#0c0e12" radius={4} className="text-center">
            <p className="px-1 py-0.5 text-[5px] font-black leading-tight text-white uppercase">{top}</p>
          </GradientChip>
          <GradientChip fill="#e8ff47" radius={4} className="text-center">
            <p className="px-1 py-0.5 text-[5px] font-black leading-tight text-black uppercase">{bottom}</p>
          </GradientChip>
        </div>
      ) : null}
      {templateId === TITLE_YELLOW_BANNER ? (
        <GradientChip fill="#e8ff47" radius={6} className="absolute top-3 inset-x-1">
          <p className="px-1 py-1.5 text-center text-[5px] font-black leading-tight text-black uppercase">
            {top}
            {bottom ? <span className="block mt-0.5">{bottom}</span> : null}
          </p>
        </GradientChip>
      ) : null}
      {templateId === TITLE_NUMBER_HOOK ? (
        <div className="absolute top-3 left-1 right-1 flex items-start gap-0.5">
          <span className="text-[16px] font-black leading-none text-[#e8ff47]">{number}</span>
          <span className="pt-1 text-[5px] font-black uppercase leading-tight text-white">{rest}</span>
        </div>
      ) : null}
      {templateId === TITLE_MARKER_HIGHLIGHT ? (
        <div className="absolute top-3 inset-x-1 space-y-1 text-center">
          <p className="text-[5px] font-black uppercase leading-tight text-white">{top}</p>
          <GradientChip fill="#fde047" radius={4} className="inline-block">
            <p className="px-1 py-0.5 text-[5px] font-black uppercase leading-tight text-black">{bottom}</p>
          </GradientChip>
        </div>
      ) : null}
      {templateId === TITLE_ALERT_KICKER ? (
        <div className="absolute top-2 inset-x-1 space-y-1">
          <GradientChip fill="#dc2626" radius={4} className="inline-block">
            <p className="px-1 py-0.5 text-[4px] font-black uppercase text-white">{clip(top, 12) || 'WATCH THIS'}</p>
          </GradientChip>
          <GradientChip fill="rgba(12,14,18,0.94)" radius={6}>
            <p className="px-1 py-1.5 text-center text-[5px] font-black uppercase leading-tight text-white">
              {bottom || top}
            </p>
          </GradientChip>
        </div>
      ) : null}
      {templateId === TITLE_BLACK_CAPSULE ? (
        <GradientChip fill="rgba(12,14,18,0.94)" radius={8} className="absolute top-3 inset-x-1">
          <div className="px-1 py-2 text-center">
            <p className="text-[5px] font-black uppercase leading-tight text-white">{top}</p>
            {bottom ? (
              <p className="mt-0.5 text-[5px] font-black uppercase leading-tight text-[#e8ff47]">{bottom}</p>
            ) : null}
          </div>
        </GradientChip>
      ) : null}
    </div>
  );
}

export function ReelsTitleFrame({ templateId, line1, line2, style }) {
  const look = sanitizeTitleStyle(style);
  const fill = titleFillCss(look);
  const type = titleTypeCss(look, { fontSize: 12 });
  const top = displayTitleText(line1, look);
  const bottom = displayTitleText(line2, look);
  if (!top && !bottom) return null;
  const numberMatch = `${top} ${bottom}`.trim().match(/^(\d+)\s*(.*)$/);
  const number = top && numberMatch?.[1] ? numberMatch[1] : '';
  const rest = (number ? (numberMatch?.[2] || bottom || '').trim() : (bottom || top));
  const id = templateId || TITLE_IMPACT_STACK;

  return (
    <div className="flex h-full w-full flex-col items-center justify-start gap-1.5 px-1 pt-0.5 pointer-events-none">
      <div className="pointer-events-auto flex max-w-full flex-col items-center gap-1.5">
      {id === TITLE_IMPACT_STACK ? (
        <>
          {top ? (
            <GradientChip fill="#0c0e12" radius={10} padding={2} className="max-w-full">
              <p className="text-center leading-tight tracking-wide text-white" style={{ ...type, color: '#ffffff' }}>
                {top}
              </p>
            </GradientChip>
          ) : null}
          {bottom ? (
            <GradientChip fill={fill} radius={10} padding={2} className="max-w-full">
              <p className="text-center leading-tight tracking-wide" style={type}>
                {bottom}
              </p>
            </GradientChip>
          ) : null}
        </>
      ) : null}
      {id === TITLE_YELLOW_BANNER ? (
        <GradientChip fill={fill} radius={12} padding={2.5} className="max-w-full">
          <p className="text-center leading-tight" style={type}>
            {top || null}
            {top && bottom ? <span className="block mt-0.5">{bottom}</span> : null}
            {!top && bottom ? bottom : null}
          </p>
        </GradientChip>
      ) : null}
      {id === TITLE_NUMBER_HOOK ? (
        <div className="flex w-full items-start gap-2 px-1">
          {number ? (
            <span className="leading-none" style={{ ...type, fontSize: `${Math.round(28 * look.font_size)}px`, color: look.fill_color, padding: 0 }}>{number}</span>
          ) : null}
          {rest ? (
            <p className="pt-1 leading-tight text-white" style={{ ...type, color: '#ffffff', padding: 0 }}>{rest}</p>
          ) : null}
        </div>
      ) : null}
      {id === TITLE_MARKER_HIGHLIGHT ? (
        <>
          {top ? (
            <p className="text-center leading-tight text-white" style={{ ...type, color: '#ffffff', padding: 0 }}>{top}</p>
          ) : null}
          {bottom ? (
            <GradientChip fill={fill} radius={8} padding={2}>
              <p className="text-center leading-tight" style={type}>{bottom}</p>
            </GradientChip>
          ) : null}
        </>
      ) : null}
      {id === TITLE_ALERT_KICKER ? (
        <>
          {top ? (
            <GradientChip fill="#dc2626" radius={6} padding={2} className="self-start">
              <p className="text-white" style={{ ...type, fontSize: `${Math.round(9 * look.font_size)}px`, color: '#ffffff', padding: `${Math.round(look.pad_y * 0.18)}px ${Math.round(look.pad_x * 0.22)}px` }}>{top.slice(0, 28)}</p>
            </GradientChip>
          ) : null}
          {bottom ? (
            <GradientChip fill="rgba(12,14,18,0.94)" radius={12} padding={2} className="w-full">
              <p className="text-center leading-tight text-white" style={{ ...type, color: '#ffffff' }}>
                {bottom}
              </p>
            </GradientChip>
          ) : null}
        </>
      ) : null}
      {id === TITLE_BLACK_CAPSULE ? (
        <GradientChip fill="rgba(12,14,18,0.94)" radius={16} padding={2.5} className="max-w-full">
          <div className="text-center" style={{ padding: `${Math.round(look.pad_y * 0.32)}px ${Math.round(look.pad_x * 0.32)}px` }}>
            {top ? (
              <p className="leading-tight text-white" style={{ ...type, color: '#ffffff', padding: 0 }}>{top}</p>
            ) : null}
            {bottom ? (
              <p className={`${top ? 'mt-1' : ''} leading-tight`} style={{ ...type, padding: 0 }}>{bottom}</p>
            ) : null}
          </div>
        </GradientChip>
      ) : null}
      </div>
    </div>
  );
}

export function ReelsShortsTitlePicker({ project, saving = false, onSave }) {
  const overlay = sanitizeTitleOverlay(project?.editor_cut?.title_overlay, project?.title);
  const [line1, setLine1] = useState(overlay.line1);
  const [line2, setLine2] = useState(overlay.line2);
  const [style, setStyle] = useState(overlay.style);

  useEffect(() => {
    setLine1(overlay.line1);
    setLine2(overlay.line2);
    setStyle(overlay.style);
  }, [
    overlay.line1,
    overlay.line2,
    overlay.template_id,
    overlay.enabled,
    overlay.style?.font_family,
    overlay.style?.font_size,
    overlay.style?.uppercase,
    overlay.style?.fill_template,
    overlay.style?.fill_color,
    overlay.style?.fill_color2,
    overlay.style?.text_color,
    overlay.style?.pad_x,
    overlay.style?.pad_y,
    project?.id,
  ]);

  const commit = (next, message) => {
    if (!onSave) return;
    const nextStyle = sanitizeTitleStyle(next.style ?? style);
    if (next.style) setStyle(nextStyle);
    onSave(
      {
        enabled: next.enabled,
        template_id: next.template_id || overlay.template_id,
        line1: String(next.line1 ?? line1).trim().slice(0, 80),
        line2: String(next.line2 ?? line2).trim().slice(0, 80),
        style: nextStyle,
      },
      message,
    );
  };

  const projectId = project?.id || null;

  return (
    <div
      className="rounded-xl border border-[#1f2635] bg-[#12151d] p-4 space-y-3"
      data-testid="reels-shorts-title-templates"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Type className="w-4 h-4 text-yellow-300 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">Hook title</p>
            <p className="text-[11px] text-slate-500">
              Instagram-style stacked bars at the top of the reel. Pick a template, then regenerate the final video.
            </p>
          </div>
        </div>
        <Switch
          checked={overlay.enabled}
          disabled={saving || !projectId}
          onCheckedChange={(next) => commit(
            { enabled: next, template_id: overlay.template_id, line1, line2 },
            next ? 'Title overlay on' : 'Title overlay off',
          )}
          data-testid="reels-shorts-title-toggle"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Line 1</span>
          <Input
            value={line1}
            maxLength={80}
            disabled={saving || !projectId}
            onChange={(event) => setLine1(event.target.value)}
            onBlur={() => {
              if (line1.trim() === overlay.line1 && line2.trim() === overlay.line2) return;
              commit(
                { enabled: overlay.enabled || Boolean(line1.trim() || line2.trim()), template_id: overlay.template_id, line1, line2 },
                'Title text saved',
              );
            }}
            placeholder="6 CANCER CAUSING"
            className="h-8 bg-black/40 border-[#2a3446] text-xs text-white uppercase"
            data-testid="reels-shorts-title-line1"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Line 2</span>
          <Input
            value={line2}
            maxLength={80}
            disabled={saving || !projectId}
            onChange={(event) => setLine2(event.target.value)}
            onBlur={() => {
              if (line1.trim() === overlay.line1 && line2.trim() === overlay.line2) return;
              commit(
                { enabled: overlay.enabled || Boolean(line1.trim() || line2.trim()), template_id: overlay.template_id, line1, line2 },
                'Title text saved',
              );
            }}
            placeholder="BEAUTY / SKINCARE PRODUCTS"
            className="h-8 bg-black/40 border-[#2a3446] text-xs text-white uppercase"
            data-testid="reels-shorts-title-line2"
          />
        </label>
      </div>

      <TitleStyleControls
        style={style}
        disabled={saving || !projectId}
        onChange={(nextStyle, message) => commit(
          { enabled: overlay.enabled || Boolean(line1.trim() || line2.trim()), template_id: overlay.template_id, line1, line2, style: nextStyle },
          message,
        )}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {REELS_SHORTS_TITLE_TEMPLATES.map((row) => {
          const selected = overlay.enabled && overlay.template_id === row.id;
          return (
            <button
              key={row.id}
              type="button"
              disabled={saving || !projectId}
              onClick={() => commit(
                { enabled: true, template_id: row.id, line1, line2 },
                `${row.label} title applied`,
              )}
              className={`flex gap-2 rounded-xl border p-2 text-left transition ${
                selected
                  ? 'border-yellow-300/60 bg-yellow-400/10 ring-1 ring-yellow-300/40'
                  : 'border-[#1f2635] bg-black/30 hover:border-white/20'
              } disabled:opacity-50`}
              data-testid={`reels-shorts-title-template-${row.id}`}
            >
              <TitlePreview templateId={row.id} line1={line1} line2={line2} />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-white leading-tight">{row.label}</p>
                <p className="mt-1 text-[10px] text-slate-500 leading-snug">{row.description}</p>
              </div>
            </button>
          );
        })}
      </div>
      {saving ? (
        <p className="text-[11px] text-slate-400 inline-flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Saving title…
        </p>
      ) : (
        <p className="text-[11px] text-slate-500">
          {overlay.enabled
            ? 'Generate the final video to burn this title, font, and fill onto every clip.'
            : 'Turn on or tap a template to add a hook title like the stacked black / yellow bars on Instagram Reels.'}
        </p>
      )}
    </div>
  );
}

export function ReelsShortsTitleCatalog() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="reels-shorts-title-catalog">
      {REELS_SHORTS_TITLE_TEMPLATES.map((row) => (
        <div
          key={row.id}
          className="flex gap-2 rounded-xl border border-[#1f2635] bg-[#12151d] p-2"
        >
          <TitlePreview
            templateId={row.id}
            line1="6 CANCER CAUSING"
            line2="BEAUTY / SKINCARE PRODUCTS"
          />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-white leading-tight">{row.label}</p>
            <p className="mt-1 text-[10px] text-slate-500 leading-snug">{row.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
